import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/lib/supabase";
import { Mail, Lock, Loader2 } from "lucide-react";
import { translations } from "@/lib/translations";
import { Link } from "react-router-dom";
import { useShufersalCredentials } from '@/lib/hooks/useShufersalCredentials';
import { ShufersalCredentialsForm } from './ShufersalCredentialsForm';

type AuthMode = "login" | "signup" | "reset";

export default function AuthForm() {
  const [mode, setMode] = useState<AuthMode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const { hasCredentials, loading: checkingCredentials, checkCredentials } = useShufersalCredentials();
  const [showShufersalForm, setShowShufersalForm] = useState(false);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log("handleAuth called with mode:", mode);
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      if (mode === "login") {
        console.log("Attempting to sign in with email:", email);
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (signInError) {
          console.error("Sign-in error:", signInError);
          throw signInError;
        }
        console.log("Sign in succeeded, fetching session...");

        const { data: { session } } = await supabase.auth.getSession();
        console.log("session after sign in:", session);

        if (session?.user) {
          console.log("User object is present, checking Shufersal credentials...");
          await checkCredentials();
          await new Promise(resolve => setTimeout(resolve, 100));
          console.log("hasCredentials:", hasCredentials);
          if (!hasCredentials) {
            console.log("No Shufersal credentials found, showing form...");
            setShowShufersalForm(true);
          }
        } else {
          console.log("No user found in session, skipping credentials check.");
        }

      } else if (mode === "signup") {
        console.log("Attempting to sign up with email:", email);
        const { error: signUpError } = await supabase.auth.signUp({
          email,
          password,
        });
        if (signUpError) {
          console.error("Sign-up error:", signUpError);
          throw signUpError;
        }
        setMessage(translations.auth.checkEmail);
        console.log("Sign up succeeded, fetching session...");

        const { data: { session } } = await supabase.auth.getSession();
        console.log("session after sign up:", session);

        if (session?.user) {
          console.log("User object is present, checking Shufersal credentials...");
          await checkCredentials();
          console.log("hasCredentials:", hasCredentials);
          if (!hasCredentials) {
            console.log("No Shufersal credentials found, showing form...");
            setShowShufersalForm(true);
          }
        } else {
          console.log("No user found in session, skipping credentials check.");
        }

      } else if (mode === "reset") {
        console.log("Attempting to reset password for:", email);
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email);
        if (resetError) {
          console.error("Reset error:", resetError);
          throw resetError;
        }
        setMessage(translations.auth.checkEmailReset);
      }
    } catch (err: any) {
      console.error("Auth error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    console.log("Attempting Google sign in...");
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.origin,
        },
      });
      if (error) {
        console.error("Google sign-in error:", error);
        throw error;
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  console.log("Rendering Shufersal form:", showShufersalForm);

  return (
    <Card className="w-full max-w-md p-6 bg-white">
      {showShufersalForm ? (
        <ShufersalCredentialsForm onComplete={() => setShowShufersalForm(false)} />
      ) : (
        <form onSubmit={handleAuth} className="space-y-6">
          <div className="space-y-2 text-center">
            <h2 className="text-2xl font-bold">
              {mode === "login"
                ? translations.auth.welcomeBack
                : mode === "signup"
                  ? translations.auth.createAccount
                  : translations.auth.resetPassword}
            </h2>
            <p className="text-muted-foreground">
              {mode === "login"
                ? translations.auth.signInToAccount
                : mode === "signup"
                  ? translations.auth.signUpForAccount
                  : translations.auth.enterEmailToReset}
            </p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {message && (
            <Alert>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{translations.auth.email}</Label>
              <div className="relative">
                <Mail className="absolute right-3 top-3 h-4 w-4 text-muted-foreground rtl-icon" />
                <Input
                  id="email"
                  type="email"
                  placeholder={translations.auth.enterEmail}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rtl-input"
                  required
                />
              </div>
            </div>

            {mode !== "reset" && (
              <div className="space-y-2">
                <Label htmlFor="password">{translations.auth.password}</Label>
                <div className="relative">
                  <Lock className="absolute right-3 top-3 h-4 w-4 text-muted-foreground rtl-icon" />
                  <Input
                    id="password"
                    type="password"
                    placeholder={translations.auth.enterPassword}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="rtl-input"
                    required
                  />
                </div>
              </div>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
            {mode === "login"
              ? translations.auth.signIn
              : mode === "signup"
                ? translations.auth.signUp
                : translations.auth.resetPassword}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-muted-foreground">
                או המשך עם
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleGoogleSignIn}
          >
            <img
              src="https://www.google.com/favicon.ico"
              alt="Google"
              className="h-4 w-4 ml-2"
            />
            התחבר עם Google
          </Button>

          <div className="space-y-2 text-center text-sm">
            {mode === "login" ? (
              <>
                <p>
                  {translations.auth.dontHaveAccount}{" "}
                  <button
                    type="button"
                    onClick={() => setMode("signup")}
                    className="text-primary hover:underline"
                  >
                    {translations.auth.signUp}
                  </button>
                </p>
                <button
                  type="button"
                  onClick={() => setMode("reset")}
                  className="text-primary hover:underline"
                >
                  {translations.auth.forgotPassword}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setMode("login")}
                className="text-primary hover:underline"
              >
                {translations.auth.backToLogin}
              </button>
            )}

            {mode === "signup" && (
              <div className="text-xs text-muted-foreground text-center mt-4">
                על ידי יצירת חשבון, אתה מסכים ל
                <Link to="/terms" className="text-primary hover:underline mx-1">
                  תנאי השימוש
                </Link>
                ול
                <Link to="/privacy" className="text-primary hover:underline mx-1">
                  מדיניות הפרטיות
                </Link>
                שלנו
              </div>
            )}
          </div>
        </form>
      )}
    </Card>
  );
}
